/**
 * CheckoutService - Handles Dutchie checkout integration
 * 
 * This service manages the checkout flow by interfacing with Dutchie's hosted
 * checkout system. It creates checkout sessions and handles the redirect flow
 * to complete orders through Dutchie's compliance-verified payment processing.
 */

export interface CheckoutMetadata {
  customerInfo?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
  orderNotes?: string;
  marketingOptIn?: boolean;
}

export interface CheckoutSession {
  id: string;
  redirectUrl: string;
  status: 'CREATED' | 'PENDING' | 'COMPLETED' | 'EXPIRED';
  createdAt: string;
  expiresAt: string;
}

export interface CreateCheckoutRequest {
  cartId: string;
  orderType: 'DELIVERY' | 'PICKUP';
  pricingType: 'MEDICAL' | 'RECREATIONAL';
  retailerId: string;
  returnUrl?: string;
  metadata?: CheckoutMetadata;
}

export interface CreateCheckoutResponse {
  success: boolean;
  data: CheckoutSession;
  error?: string;
}

export class CheckoutService {
  private static baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/checkout`;

  /**
   * Creates a Dutchie checkout session for the given cart
   * 
   * @param request - Checkout creation parameters
   * @returns Promise resolving to checkout session with redirect URL
   * @throws Error if checkout creation fails
   */
  public static async createCheckout(request: CreateCheckoutRequest): Promise<CheckoutSession> {
    try {
      // First, try the new checkout endpoint
      try {
        const response = await fetch(`${this.baseUrl}/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            cartId: request.cartId,
            orderType: request.orderType,
            pricingType: request.pricingType,
            returnUrl: request.returnUrl || `${window.location.origin}/order-confirmation`,
            metadata: request.metadata || {}
          })
        });

        if (response.ok) {
          const data: CreateCheckoutResponse = await response.json();

          if (data.success) {
            return data.data;
          }
        }
      } catch (endpointError) {
        // New checkout endpoint not available, using cart-based approach
      }

      // Fallback: Check if cart already has a redirectUrl from Dutchie
      const cartResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/${request.cartId}?retailerId=${encodeURIComponent(request.retailerId)}`);
      
      if (!cartResponse.ok) {
        throw new Error('Failed to retrieve cart for checkout');
      }

      const cartData = await cartResponse.json();
      
      if (cartData.success && cartData.data.redirectUrl) {
        return {
          id: `cart-${request.cartId}`,
          redirectUrl: cartData.data.redirectUrl,
          status: 'CREATED' as const,
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 minutes
        };
      }

      // If no redirectUrl available, show helpful error
      throw new Error('Checkout not yet implemented. Please contact support to complete your order.');
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Retrieves checkout session details
   *
   * @param checkoutId - The checkout session ID
   * @returns Promise resolving to checkout session details
   */
  public static async getCheckout(checkoutId: string): Promise<CheckoutSession> {
    try {
      const response = await fetch(`${this.baseUrl}/${checkoutId}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: CreateCheckoutResponse = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to retrieve checkout session');
      }

      return data.data;
    } catch (error) {
      console.error('Error retrieving checkout session:', error);
      throw error;
    }
  }

  /**
   * Initiates the checkout redirect flow
   *
   * @param checkoutSession - The checkout session containing redirect URL
   */
  public static initiateCheckout(checkoutSession: CheckoutSession): void {
    // Track checkout redirect event and add_payment_info (GA4 standard event)
    import('./AnalyticsService.ts').then(({ default: analyticsService }) => {
      analyticsService.trackEvent('checkout_redirect', {
        checkout_session_id: checkoutSession.id,
        redirect_url: checkoutSession.redirectUrl,
        destination_platform: 'dutchie',
        checkout_status: checkoutSession.status,
        session_expires_at: checkoutSession.expiresAt
      });
      
      // Track add_payment_info for GA4 Enhanced Ecommerce
      analyticsService.trackEvent('add_payment_info', {
        currency: 'USD',
        payment_type: 'dutchie_hosted',
        checkout_session_id: checkoutSession.id,
        destination_platform: 'dutchie'
      });
    });
    
    // Redirect to Dutchie's hosted checkout page
    window.location.href = checkoutSession.redirectUrl;
  }

  /**
   * Builds checkout metadata from cart and user context
   * 
   * @param customerEmail - Optional customer email
   * @param orderNotes - Optional order notes
   * @returns Formatted checkout metadata
   */
  public static buildCheckoutMetadata(
    customerEmail?: string,
    orderNotes?: string
  ): CheckoutMetadata {
    const metadata: CheckoutMetadata = {};

    if (customerEmail) {
      metadata.customerInfo = {
        email: customerEmail
      };
    }

    if (orderNotes) {
      metadata.orderNotes = orderNotes;
    }

    return metadata;
  }

  /**
   * Validates checkout prerequisites
   * 
   * @param cartId - Cart ID to validate
   * @param orderType - Order type to validate
   * @param pricingType - Pricing type to validate
   * @param retailerId - Retailer ID to validate
   * @throws Error if validation fails
   */
  public static validateCheckoutRequest(
    cartId: string,
    orderType: 'DELIVERY' | 'PICKUP',
    pricingType: 'MEDICAL' | 'RECREATIONAL',
    retailerId: string
  ): void {
    if (!cartId || cartId.trim().length === 0) {
      throw new Error('Cart ID is required for checkout');
    }

    if (!orderType || !['DELIVERY', 'PICKUP'].includes(orderType)) {
      throw new Error('Valid order type (DELIVERY or PICKUP) is required');
    }

    if (!pricingType || !['MEDICAL', 'RECREATIONAL'].includes(pricingType)) {
      throw new Error('Valid pricing type (MEDICAL or RECREATIONAL) is required');
    }

    if (!retailerId || retailerId.trim().length === 0) {
      throw new Error('Retailer ID is required for checkout');
    }
  }
}